// src/components/ScenarioPickerTools.jsx
import React, { useState } from "react";
import { Alert, Box, Button, TextField, Typography } from "@mui/material";
import { matchScenarioIds, mergeScenarioIds, parseScenarioList } from "../utils/scenarioIds";

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

const pasteMessage = ({ added, alreadySelected, unknown, asofdt }) => {
  const parts = [added > 0 ? `Added ${plural(added, "scenario")}` : "No scenarios added"];
  if (alreadySelected > 0) parts.push(`${alreadySelected} already selected`);
  let message = `${parts.join(", ")}.`;
  if (unknown.length > 0) {
    message += ` Not available on ${asofdt}, so not added: ${unknown.join(", ")}.`;
  }
  return message;
};

/**
 * Bulk controls under the Scenarios picker: a full run holds 44 to 162 scenarios,
 * too many to tick one at a time. `available` is the list for `asofdt`; every change
 * goes through `onChange` with the whole new selection.
 */
const ScenarioPickerTools = ({ available, selected, asofdt, loading, onChange }) => {
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [result, setResult] = useState(null);
  // Nothing is matched against a list that is still loading or did not load.
  const ready = !loading && available.length > 0;

  const addPasted = () => {
    const { known, unknown } = matchScenarioIds(parseScenarioList(pasteText), available);
    const merged = mergeScenarioIds(selected, known);
    const added = merged.length - selected.length;
    onChange(merged);
    setResult({ added, alreadySelected: known.length - added, unknown, asofdt });
    // The IDs the date lacks stay in the box, to correct or delete.
    setPasteText(unknown.join("\n"));
  };

  const togglePaste = () => {
    setPasteOpen((wasOpen) => !wasOpen);
    setPasteText("");
    setResult(null);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          disabled={!ready}
          onClick={() => onChange([...available].sort())}
        >
          Select all ({available.length})
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={selected.length === 0}
          onClick={() => onChange([])}
        >
          Clear all
        </Button>
        <Button
          size="small"
          variant={pasteOpen ? "contained" : "outlined"}
          aria-expanded={pasteOpen}
          onClick={togglePaste}
        >
          Paste list
        </Button>
        <Typography variant="caption" color="text.secondary">
          {loading || !asofdt
            ? `${selected.length} selected`
            : `${selected.length} selected, ${available.length} available on ${asofdt}`}
        </Typography>
      </Box>
      {pasteOpen && (
        <Box sx={{ mt: 1 }}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Scenario IDs to add"
            value={pasteText}
            onChange={(event) => {
              setPasteText(event.target.value);
              setResult(null);
            }}
            placeholder={"US0001, US0002\nor one ID per line"}
            helperText="Separate IDs with commas, spaces or new lines"
          />
          <Button
            size="small"
            variant="outlined"
            sx={{ mt: 1 }}
            disabled={!ready || parseScenarioList(pasteText).length === 0}
            onClick={addPasted}
          >
            Add to selection
          </Button>
          {/* A result names the date it was checked against; a new date hides it. */}
          {result && result.asofdt === asofdt && (
            <Alert severity={result.unknown.length > 0 ? "warning" : "success"} sx={{ mt: 1 }}>
              {pasteMessage(result)}
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ScenarioPickerTools;
